import { useMemo } from 'react';
import { Badge, Box, Switch, Tooltip } from '@chakra-ui/react';
import { BsThreeDotsVertical } from 'react-icons/bs';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import SSOStatusCellRenderer from '../SSOStatusCellRenderer';
import MicrosoftProviderPreview from './MicrosoftProviderPreview';
import tcapWhiteLogo from 'assets/images/TCAP_White_Small.png';
import { providersOption, providerTableWrapperStyles } from '../constants';

const tcapRow = { isTcapDefault: true };

export default function SSOProvidersTable({
    providers,
    gridRef,
    refetch,
    toolbar,
    isEnforced,
    darkMode,
    switchDisabled,
    verifyMutationPending,
    ufVerifyBtn,
    ufActiveBtn,
    handleVerify,
    handleSwitchChange,
    onOpen,
}) {
    const headingColor = darkMode ? 'white' : '#1a202c';

    const columnDefs = useMemo(
        () => [
            {
                field: 'preview',
                headerName: 'Preview',
                width: 260,
                minWidth: 260,
                sortable: false,
                filter: false,
                cellStyle: (params) => ({
                    display: 'flex',
                    alignItems: 'center',
                    opacity: params.data.isTcapDefault && isEnforced ? 0.4 : 1,
                    cursor:
                        params.data.isTcapDefault && isEnforced
                            ? 'not-allowed'
                            : 'default',
                }),
                cellRenderer: (params) => {
                    if (params.data.isTcapDefault) {
                        return (
                            <Box
                                display="inline-flex"
                                alignItems="center"
                                justifyContent="center"
                                borderRadius="0.25rem"
                                h="32px"
                                w="fit-content"
                                minW="240px"
                                px={2}
                                bg="#3EAFEB"
                                userSelect="none">
                                <img
                                    src={tcapWhiteLogo}
                                    alt="TCAP Login"
                                    style={{
                                        height: '100%',
                                        objectFit: 'contain',
                                    }}
                                />
                            </Box>
                        );
                    }
                    return (
                        <MicrosoftProviderPreview
                            h="32px"
                            minW="240px"
                            borderColor="#8c8c8c"
                            color="#5e5e5e"
                            opacity={params.data.isEnabled ? 1 : 0.4}
                        />
                    );
                },
            },
            {
                field: 'name',
                headerName: 'Identity Provider',
                flex: 1,
                cellStyle: (params) => ({
                    display: 'flex',
                    alignItems: 'center',
                    opacity: params.data.isTcapDefault && isEnforced ? 0.4 : 1,
                    cursor:
                        params.data.isTcapDefault && isEnforced
                            ? 'not-allowed'
                            : 'default',
                }),
                cellRenderer: (params) => {
                    if (params.data.isTcapDefault) {
                        return (
                            <Box
                                fontSize="sm"
                                fontWeight="medium"
                                sx={{
                                    color: `${headingColor} !important`,
                                }}>
                                TCAP Username &amp; Password
                            </Box>
                        );
                    }
                    return (
                        <Box display="flex" alignItems="center" gap={2}>
                            <Box
                                fontSize="sm"
                                fontWeight="medium"
                                sx={{
                                    color: `${headingColor} !important`,
                                }}>
                                {
                                    providersOption.find(
                                        (a) => a.value === params.data.provider,
                                    )?.name
                                }{' '}
                                SSO
                            </Box>
                            {params.data.isBoth && (
                                <Badge
                                    display="flex"
                                    alignItems="center"
                                    colorScheme="messenger"
                                    borderRadius="full"
                                    maxH="26px"
                                    size="0.75rem"
                                    px={2}
                                    textTransform="capitalize">
                                    Domain Restricted
                                </Badge>
                            )}
                        </Box>
                    );
                },
            },
            {
                field: 'isVerified',
                headerName: 'Status',
                width: 140,
                cellStyle: { display: 'flex', alignItems: 'center' },
                cellRenderer: (params) => {
                    if (params.data.isTcapDefault) {
                        return (
                            <SSOStatusCellRenderer
                                status={isEnforced ? 'disabled' : 'available'}
                            />
                        );
                    }
                    if (params.data.isVerified) {
                        return <SSOStatusCellRenderer status="verified" />;
                    }
                    return (
                        <SSOStatusCellRenderer
                            data-walkthroughid={
                                ufVerifyBtn +
                                providersOption.find(
                                    (a) => a.value === params.data.provider,
                                )?.name
                            }
                            status="verify"
                            isLoading={verifyMutationPending}
                            onClick={(e) => {
                                e.stopPropagation();
                                handleVerify();
                            }}
                        />
                    );
                },
            },
            {
                field: 'isEnabled',
                headerName: 'Enable',
                width: 90,
                cellStyle: { display: 'flex', alignItems: 'center' },
                cellRenderer: (params) => {
                    if (params.data.isTcapDefault) {
                        return null;
                    }
                    return (
                        <Switch
                            data-walkthroughid={
                                ufActiveBtn +
                                providersOption.find(
                                    (a) => a.value === params.data.provider,
                                )?.name
                            }
                            isDisabled={
                                !params.data.isVerified || switchDisabled
                            }
                            id={params.node.rowIndex}
                            onChange={(e) => handleSwitchChange(e)}
                            isChecked={params.data.isEnabled}
                            size="md"
                        />
                    );
                },
            },
            {
                maxWidth: 100,
                headerName: 'Action',
                filter: false,
                flex: 1,
                suppressMovable: true,
                lockVisible: true,
                suppressHeaderMenuButton: true,
                suppressColumnsToolPanel: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                lockPosition: 'right',
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }),
                cellRenderer: (params) => {
                    if (params.data.isTcapDefault) {
                        return null;
                    }
                    return (
                        <Tooltip label="Edit">
                            <span>
                                <BsThreeDotsVertical
                                    fill={darkMode ? 'white' : 'black'}
                                    cursor="pointer"
                                    color="gray.500"
                                    fontSize="16"
                                    _hover={{ color: 'blue.500' }}
                                    onClick={() => onOpen()}
                                />
                            </span>
                        </Tooltip>
                    );
                },
            },
        ],
        [
            headingColor,
            isEnforced,
            switchDisabled,
            verifyMutationPending,
            ufVerifyBtn,
            ufActiveBtn,
            handleVerify,
            handleSwitchChange,
            onOpen,
            darkMode,
        ],
    );

    return (
        <Box mb={6} sx={providerTableWrapperStyles}>
            <AGGridMain
                tableData={{
                    columns: columnDefs,
                    data: [tcapRow, ...(providers || [])],
                }}
                gridRef={gridRef}
                getRowStyle={(params) =>
                    params.data &&
                    !params.data.isTcapDefault &&
                    !params.data.isVerified
                        ? {
                              background: darkMode
                                  ? 'rgba(255, 162, 23, 0.15)'
                                  : '#fff7e6',
                          }
                        : undefined
                }
                refetch={refetch}
                suppressPaginationPanel={true}
                domLayout="autoHeight"
                titleName="sso"
                useFixedHeight={false}
                customHeight="auto"
                extraComponents={toolbar}
            />
        </Box>
    );
}
